import type {CopNetworkObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/CopNetwork';
import type {MaterialsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import type {GeoObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {EventsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/EventsNetwork';
import type {PerspectiveCameraObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/PerspectiveCamera';
import type {RootManagerNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		function create_MAT(geo1: GeoObjNode) {
			var MAT = geo1.createNode('materialsNetwork');
			MAT.setName('MAT');
			function create_meshPhong1(MAT: MaterialsNetworkSopNode) {
				var meshPhong1 = MAT.createNode('meshPhong');
				meshPhong1.setName('meshPhong1');
				meshPhong1.uiData.setPosition(-50, -50);
				meshPhong1.p.color.set([0.9333333333333333, 0.8, 0.6470588235294118]);
				meshPhong1.params.postCreateSpareParams();
				meshPhong1.params.runOnSceneLoadHooks();
				return meshPhong1;
			}
			function create_meshPhong2(MAT: MaterialsNetworkSopNode) {
				var meshPhong2 = MAT.createNode('meshPhong');
				meshPhong2.setName('meshPhong2');
				meshPhong2.uiData.setPosition(-50, 100);
				meshPhong2.params.postCreateSpareParams();
				meshPhong2.params.runOnSceneLoadHooks();
				return meshPhong2;
			}
			function create_meshPhong3(MAT: MaterialsNetworkSopNode) {
				var meshPhong3 = MAT.createNode('meshPhong');
				meshPhong3.setName('meshPhong3');
				meshPhong3.uiData.setPosition(-50, 250);
				meshPhong3.p.useVertexColors.set(true);
				meshPhong3.p.useEnvMap.set(true);
				meshPhong3.p.envMap.set('../../../COP/envMap');
				meshPhong3.p.reflectivity.set(0.93);
				meshPhong3.params.postCreateSpareParams();
				meshPhong3.params.runOnSceneLoadHooks();
				return meshPhong3;
			}
			create_meshPhong1(MAT);
			create_meshPhong2(MAT);
			create_meshPhong3(MAT);
			MAT.uiData.setPosition(-350, 200);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return MAT;
		}
		function create_color1(geo1: GeoObjNode) {
			var color1 = geo1.createNode('color');
			color1.setName('color1');
			color1.uiData.setPosition(300, -250);
			color1.p.fromAttribute.set(true);
			color1.p.attribName.set('position');
			color1.params.postCreateSpareParams();
			color1.params.runOnSceneLoadHooks();
			return color1;
		}
		function create_color2(geo1: GeoObjNode) {
			var color2 = geo1.createNode('color');
			color2.setName('color2');
			color2.uiData.setComment(
				'we use 2 color nodes to set the color attribute of this geometry. This is then picked up by the material assigned to it.'
			);
			color2.uiData.setPosition(300, -150);
			color2.p.attribName.set('position');
			color2.p.color.set(['@Cd.r', 'if(@P.y>0.5,1,0)', '@Cd.g']);
			color2.params.postCreateSpareParams();
			color2.params.runOnSceneLoadHooks();
			return color2;
		}
		function create_fileGLTF1(geo1: GeoObjNode) {
			var fileGLTF1 = geo1.createNode('fileGLTF');
			fileGLTF1.setName('fileGLTF1');
			fileGLTF1.uiData.setPosition(50, -800);
			fileGLTF1.params.postCreateSpareParams();
			fileGLTF1.params.runOnSceneLoadHooks();
			return fileGLTF1;
		}
		function create_hierarchy1(geo1: GeoObjNode) {
			var hierarchy1 = geo1.createNode('hierarchy');
			hierarchy1.setName('hierarchy1');
			hierarchy1.uiData.setPosition(50, -700);
			hierarchy1.p.mode.set(1);
			hierarchy1.params.postCreateSpareParams();
			hierarchy1.params.runOnSceneLoadHooks();
			return hierarchy1;
		}
		function create_material1(geo1: GeoObjNode) {
			var material1 = geo1.createNode('material');
			material1.setName('material1');
			material1.uiData.setPosition(-150, 0);
			material1.p.material.set('../MAT/meshPhong1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return material1;
		}
		function create_material2(geo1: GeoObjNode) {
			var material2 = geo1.createNode('material');
			material2.setName('material2');
			material2.uiData.setPosition(50, 0);
			material2.p.material.set('../MAT/meshPhong2');
			material2.params.postCreateSpareParams();
			material2.params.runOnSceneLoadHooks();
			return material2;
		}
		function create_material3(geo1: GeoObjNode) {
			var material3 = geo1.createNode('material');
			material3.setName('material3');
			material3.uiData.setPosition(300, 0);
			material3.p.material.set('../MAT/meshPhong3');
			material3.params.postCreateSpareParams();
			material3.params.runOnSceneLoadHooks();
			return material3;
		}
		function create_merge1(geo1: GeoObjNode) {
			var merge1 = geo1.createNode('merge');
			merge1.setName('merge1');
			merge1.uiData.setPosition(50, 350);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return merge1;
		}
		function create_transform4(geo1: GeoObjNode) {
			var transform4 = geo1.createNode('transform');
			transform4.setName('transform4');
			transform4.uiData.setPosition(-150, -400);
			transform4.p.t.set([0, 0, 0.8490942039805385]);
			transform4.params.postCreateSpareParams();
			transform4.params.runOnSceneLoadHooks();
			return transform4;
		}
		function create_transform5(geo1: GeoObjNode) {
			var transform5 = geo1.createNode('transform');
			transform5.setName('transform5');
			transform5.uiData.setPosition(50, -400);
			transform5.params.postCreateSpareParams();
			transform5.params.runOnSceneLoadHooks();
			return transform5;
		}
		function create_transform6(geo1: GeoObjNode) {
			var transform6 = geo1.createNode('transform');
			transform6.setName('transform6');
			transform6.uiData.setPosition(300, -400);
			transform6.p.t.set([0, 0, -0.8490942039805385]);
			transform6.params.postCreateSpareParams();
			transform6.params.runOnSceneLoadHooks();
			return transform6;
		}
		create_MAT(geo1);
		var color1 = create_color1(geo1);
		var color2 = create_color2(geo1);
		var fileGLTF1 = create_fileGLTF1(geo1);
		var hierarchy1 = create_hierarchy1(geo1);
		var material1 = create_material1(geo1);
		var material2 = create_material2(geo1);
		var material3 = create_material3(geo1);
		var merge1 = create_merge1(geo1);
		var transform4 = create_transform4(geo1);
		var transform5 = create_transform5(geo1);
		var transform6 = create_transform6(geo1);
		color1.setInput(0, transform6);
		color2.setInput(0, color1);
		hierarchy1.setInput(0, fileGLTF1);
		material1.setInput(0, transform4);
		material2.setInput(0, transform5);
		material3.setInput(0, color2);
		merge1.setInput(0, material1);
		merge1.setInput(1, material2);
		merge1.setInput(2, material3);
		transform4.setInput(0, hierarchy1);
		transform5.setInput(0, hierarchy1);
		transform6.setInput(0, hierarchy1);
		geo1.uiData.setPosition(-50, -150);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return geo1;
	}
	function create_grid(parentNode: RootManagerNode) {
		var grid = parentNode.createNode('geo');
		grid.setName('grid');
		function create_planeHelper1(grid: GeoObjNode) {
			var planeHelper1 = grid.createNode('planeHelper');
			planeHelper1.setName('planeHelper1');
			planeHelper1.uiData.setPosition(0, 0);
			planeHelper1.flags.display.set(true);
			planeHelper1.params.postCreateSpareParams();
			planeHelper1.params.runOnSceneLoadHooks();
			return planeHelper1;
		}
		create_planeHelper1(grid);
		grid.uiData.setPosition(-50, -250);
		grid.flags.display.set(true);
		grid.params.postCreateSpareParams();
		grid.params.runOnSceneLoadHooks();
		return grid;
	}
	function create_perspectiveCamera1(parentNode: RootManagerNode) {
		var perspectiveCamera1 = parentNode.createNode('perspectiveCamera');
		perspectiveCamera1.setName('perspectiveCamera1');
		function create_events1(perspectiveCamera1: PerspectiveCameraObjNode) {
			var events1 = perspectiveCamera1.createNode('eventsNetwork');
			events1.setName('events1');
			function create_cameraOrbitControls1(events1: EventsNetworkSopNode) {
				var cameraOrbitControls1 = events1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				cameraOrbitControls1.uiData.setPosition(150, 50);
				cameraOrbitControls1.p.target.set([0.03435322343011707, 0.41806574749855663, 0.18967993698156205]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return cameraOrbitControls1;
			}
			create_cameraOrbitControls1(events1);
			events1.uiData.setPosition(-200, 50);
			events1.params.postCreateSpareParams();
			events1.params.runOnSceneLoadHooks();
			return events1;
		}
		create_events1(perspectiveCamera1);
		perspectiveCamera1.uiData.setPosition(-200, 100);
		perspectiveCamera1.flags.display.set(true);
		perspectiveCamera1.p.t.set([2.1915021772731733, 0.7292627449766405, -0.11494622230269118]);
		perspectiveCamera1.p.r.set([-92.91231074642486, 83.39132259672208, 92.93175761011257]);
		perspectiveCamera1.p.controls.set('./events1/cameraOrbitControls1');
		perspectiveCamera1.params.postCreateSpareParams();
		perspectiveCamera1.params.runOnSceneLoadHooks();
		return perspectiveCamera1;
	}
	function create_COP(parentNode: RootManagerNode) {
		var COP = parentNode.createNode('copNetwork');
		COP.setName('COP');
		function create_envMap(COP: CopNetworkObjNode) {
			var envMap = COP.createNode('envMap');
			envMap.setName('envMap');
			envMap.uiData.setPosition(50, 200);
			envMap.params.postCreateSpareParams();
			envMap.params.runOnSceneLoadHooks();
			return envMap;
		}
		function create_imageEnv(COP: CopNetworkObjNode) {
			var imageEnv = COP.createNode('imageEXR');
			imageEnv.setName('imageEnv');
			imageEnv.uiData.setPosition(50, 100);
			imageEnv.p.url.set(
				'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/piz_compressed.exr'
			);
			imageEnv.params.postCreateSpareParams();
			imageEnv.params.runOnSceneLoadHooks();
			return imageEnv;
		}
		function create_imageUv(COP: CopNetworkObjNode) {
			var imageUv = COP.createNode('image');
			imageUv.setName('imageUv');
			imageUv.uiData.setPosition(-100, 100);
			imageUv.p.url.set('https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/textures/uv.jpg');
			imageUv.p.tflipY.set(true);
			imageUv.params.postCreateSpareParams();
			imageUv.params.runOnSceneLoadHooks();
			return imageUv;
		}
		var envMap = create_envMap(COP);
		var imageEnv = create_imageEnv(COP);
		create_imageUv(COP);
		envMap.setInput(0, imageEnv);
		COP.uiData.setPosition(-200, 200);
		COP.params.postCreateSpareParams();
		COP.params.runOnSceneLoadHooks();
		return COP;
	}
	function create_lights(parentNode: RootManagerNode) {
		var lights = parentNode.createNode('geo');
		lights.setName('lights');
		function create_hemisphereLight1(lights: GeoObjNode) {
			var hemisphereLight1 = lights.createNode('hemisphereLight');
			hemisphereLight1.setName('hemisphereLight1');
			hemisphereLight1.uiData.setPosition(-100, 0);
			hemisphereLight1.params.postCreateSpareParams();
			hemisphereLight1.params.runOnSceneLoadHooks();
			return hemisphereLight1;
		}
		function create_merge1(lights: GeoObjNode) {
			var merge1 = lights.createNode('merge');
			merge1.setName('merge1');
			merge1.uiData.setPosition(50, 300);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return merge1;
		}
		function create_polarTransform1(lights: GeoObjNode) {
			var polarTransform1 = lights.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			polarTransform1.uiData.setPosition(150, 50);
			polarTransform1.p.longitude.set(129.6);
			polarTransform1.p.latitude.set(46.8);
			polarTransform1.p.depth.set(5.8);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return polarTransform1;
		}
		function create_spotLight1(lights: GeoObjNode) {
			var spotLight1 = lights.createNode('spotLight');
			spotLight1.setName('spotLight1');
			spotLight1.uiData.setPosition(150, -150);
			spotLight1.params.postCreateSpareParams();
			spotLight1.params.runOnSceneLoadHooks();
			return spotLight1;
		}
		var hemisphereLight1 = create_hemisphereLight1(lights);
		var merge1 = create_merge1(lights);
		var polarTransform1 = create_polarTransform1(lights);
		var spotLight1 = create_spotLight1(lights);
		merge1.setInput(0, hemisphereLight1);
		merge1.setInput(1, polarTransform1);
		polarTransform1.setInput(0, spotLight1);
		lights.uiData.setPosition(-50, -50);
		lights.flags.display.set(true);
		lights.params.postCreateSpareParams();
		lights.params.runOnSceneLoadHooks();
		return lights;
	}
	var geo1 = create_geo1(root);
	create_grid(root);
	var perspectiveCamera1 = create_perspectiveCamera1(root);
	create_COP(root);
	create_lights(root);
	return {
		scene,
		cameraPath: perspectiveCamera1.path(),
		focusedNodes: [geo1.nodesByType('material')[0]],
		autoLayout: false,
	};
}
// export const matMeshPhong = [
// 	new Example({
// 		name: `${ExamplePrefix.BY_NODE}/mat/meshPhong`,
// 		createData,
// 		relatedNodes: [MeshPhongMatNode],
// 	}),
// ];
